/* eslint-disable ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
// Ember imports
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import moment from 'moment';
import { get, set, action, computed } from '@ember/object';
import { service } from '@ember/service';
import { intersect } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
// External imports
import DataType from '@linc-technologies/linced-common/enums/data-type';
// Internal imports
import InterventionStatus from 'linced-app/enums/intervention-status';
import { findBy, isEvery } from '@linc-technologies/linced-common/utils/arrays';
import {
  filterValue,
  getNumericYearLevelFilter,
} from '@linc-technologies/linced-common/utils/filtering';

export default class InterventionsController extends Controller {
  @action
  setAcademicYear(value) {
    this.academicYear = value;
  }

  @action
  setTermNumber(value) {
    this.termNumber = value;
  }

  queryParams = ['status', 'academicYear', 'termNumber'];

  @service
  applicationData;

  // Services
  @service
  session;

  @service
  dates;

  @service
  terms;

  @service
  toaster;

  InterventionStatus = InterventionStatus;

  // Attributes
  @tracked academicYear = '';

  @tracked termNumber = 0;

  // title is a dynamic title shown on the page and used in downloaded file names
  get title() {
    let title = `Attendance Interventions
    (${
      InterventionStatus.values.find((status) => status.value === this.status)
        .label
    })`;
    if (this.termNumber > 0) {
      title += ` - Term ${this.termNumber}`;
    }
    if (this.academicYear) {
      title += ` ${this.academicYear}`;
    }
    return title;
  }

  // status is the currently selected tab
  // by default, when loading the page, we're opening the "Active" tab
  @tracked status = InterventionStatus.AWAITING;

  // isCompleted indicates whether the "Completed" tab is currently selected
  get isCompleted() {
    return this.status === InterventionStatus.ACTIONED;
  }

  // termInYear gives dropdown options for the terms filter
  @computed('model.archivedInterventions.[]', 'academicYear', 'years.[]')
  get termsInYear() {
    if (isEmpty(this.model.archivedInterventions)) {
      return [];
    }
    if (!isEmpty(this.academicYear)) {
      // We have selected a year
      return [
        ...new Set(
          this.model.archivedInterventions
            .filter((item) => item.academicYear === this.academicYear)
            .map((item) => item.termNumber),
        ),
      ].sort((a, b) => a - b);
    }
    if (!isEmpty(this.years)) {
      // We don't have a year selected, but we can just grab the latest year
      // available from the records
      return [
        ...new Set(
          this.model.archivedInterventions
            .filter((item) => item.academicYear === this.years[0])
            .map((item) => item.termNumber),
        ),
      ].sort((a, b) => a - b);
    }
    // Default
    return [];
  }

  // interventions filters out the model's intervention
  // after their status has been updated
  @computed('model.interventions.@each.status', 'status')
  get interventions() {
    return this.model.interventions.filter(
      (intervention) => intervention.status === this.status,
    );
  }

  // filters holds an array of in-table filter fields and values
  @tracked filters = [];

  @computed(
    'interventions',
    'filters.@each.{field,value}',
    'session.activeSchool.yearLevels.[]',
  )
  get filteredInterventions() {
    if (isEmpty(this.filters)) {
      // No filters - just return the data.
      return this.interventions;
    }

    // Determine datatypes for any specified filters.
    let filters = this.filters.map((filter) => {
      return {
        key: filter.field,
        dataType: DataType.STRING,
        value: filter.value,
      };
    });

    // Determine numeric filter for year level, if applicable.
    let yearLevelFilterValue = getNumericYearLevelFilter(
      findBy(filters, 'key', 'person.currentYearLevel'),
      this.session.school.yearLevels,
    );

    // Apply all filters to each row in one hit, to avoid iterating over a
    // (potentially) large dataset multiple times.
    return this.interventions.filter((intervention) => {
      return filters.every((filter) => {
        // Field-specific filter processing.
        switch (filter.key) {
          case 'person.currentYearLevel':
            return filterValue(
              intervention.get('person.numericCurrentYearLevel') ?? '',
              DataType.INTEGER,
              yearLevelFilterValue,
            );
          case 'completedDate':
          case 'dueDate':
          case 'date':
            if (filter.value.length === 10) {
              // Input filter value for dates need to be formatted into the 'YYYY-MM-DD' format
              // Only format if the value is a full date
              return filterValue(
                get(intervention, filter.key) ?? '',
                filter.dataType,
                moment(filter.value, 'DD/MM/YYYY').format('YYYY-MM-DD'),
              );
            }
            return filterValue(
              get(intervention, filter.key) ?? '',
              filter.dataType,
              filter.value,
            );

          default:
            // Generic filter processing.
            return filterValue(
              get(intervention, filter.key) ?? '',
              filter.dataType,
              filter.value,
            );
        }
      });
    });
  }

  // selected holds the array of selected interventions to mark as completed/active
  @tracked selected = [];

  // clearSelection resets the selected
  clearSelection() {
    this.selected = [];
  }

  // filteredSelected filters out the interventions  not visible in the table
  // from the selected ones
  @intersect('selected', 'filteredInterventions')
  filteredSelected;

  // isActionButtonEnabled returns whether an action button for completing/re-activating
  // interventions is enabled if there are no edited unsaved notes
  // and there are selected interventions
  @computed('filteredSelected.{[],@each.hasDirtyAttributes}')
  get isActionButtonEnabled() {
    return (
      isEvery(this.filteredSelected, 'hasDirtyAttributes', false) &&
      !isEmpty(this.filteredSelected)
    );
  }

  // setTab changes tabs, resets selected interventions, and clears filters
  @action
  setTab(newStatus) {
    // Default for all tabs
    this.status = newStatus;
    this.clearSelection();
    this.filters = [];
    if (newStatus === InterventionStatus.AWAITING) {
      // We don't want to filter by year or term in the "Awaiting" tab
      this.academicYear = '';
      this.termNumber = 0;
    }
    if (newStatus === InterventionStatus.ACTIONED) {
      // Automatically select the latest year and term in filters when switching to "Completed"
      // so we can query for them straight away
      this.academicYear = this.years[0] || this.dates.currentAcademicYear;
      this.termNumber = this.termsInYear[0] || this.terms.currentTerm.term;
    }
  }

  // saveNote saves notes for an intervention using the "Save" button
  // if it's been changed
  @action
  saveNote(intervention) {
    // Copy the note before the record's reload
    let note = intervention.note;
    intervention.reload().then((intervention) => {
      intervention.setProperties({
        updateTime: moment().unix(),
        note: note,
        intervenerId: this.session.person,
      });
      intervention
        .save()
        .then(() => {
          this.toaster.success('Note for an Attendance Intervention saved.');
        })
        .catch(() => {
          this.toaster.error(
            'Error saving the note for an Attendance Intervention',
          );
        });
    });
  }

  // toggleSelected changes the state of a tick box for the selected row
  @action
  toggleSelected(intervention) {
    if (this.selected.includes(intervention)) {
      this.selected = this.selected.filter((item) => item !== intervention);
    } else {
      this.selected = [...this.selected, intervention];
    }
  }

  // toggleAll selects or de-selects all rows in the table
  @action
  toggleAll() {
    if (this.selected.length >= this.interventions.length) {
      // All or more are already selected, so
      // clear selection.
      this.clearSelection();
    } else {
      // Not all selected; select them all.
      this.selected = [...this.interventions];
    }
  }

  @action
  completeSelected() {
    let updates = this.filteredSelected.map((intervention) => {
      return intervention.reload().then((intervention) => {
        intervention.setProperties({
          updateTime: moment().unix(),
          completedDate: moment().format('YYYY-MM-DD'),
          status: InterventionStatus.ACTIONED,
          intervenerId: this.session.person,
        });

        return intervention.save();
      });
    });
    Promise.all(updates)
      .then(() => {
        this.toaster.success('Attendance Interventions actioned.');
        // Refresh the count in the left menu. (currently removed)
        //this.applicationData.awaitingInterventions.update();
      })
      .catch(() => {
        this.toaster.error('Error actioning Attendance Interventions.');
      });
  }

  @action
  reactivateSelected() {
    let updates = this.filteredSelected.map((intervention) => {
      return intervention.reload().then((intervention) => {
        intervention.setProperties({
          updatedTime: moment().unix,
          completedDate: undefined,
          status: InterventionStatus.AWAITING,
          note: intervention.note,
        });

        return intervention.save();
      });
    });

    Promise.all(updates)
      .then(() => {
        this.toaster.success('Attendance Interventions re-activated.');
        // Refresh the count in the left menu. (currently removed)
        //this.applicationData.awaitingInterventions.update();
      })
      .catch(() => {
        this.toaster.error('Error re-activating Attendance Interventions.');
      });
  }

  // filter modifies the filters object and applies it to the interventions object
  @action
  filter(field, value) {
    let filter = findBy(this.filters, 'field', field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      this.filters = [...this.filters, { field, value }];
      return;
    }
    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this.filters = this.filters.filter((item) => item !== filter);
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }
}
